import { forecastMath } from 'features/math'
import { ChartDataPoint } from 'features/systemLens'
import {
  BaseSensorEntry,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

const { evaluate } = forecastMath

// Sanitize sensor ID for use in expressions (remove dashes and periods)
export const sanitizeSensorId = (id: string): string => {
  return id.replace(/-/g, '_').replace(/\./g, '_')
}

// Get sensor name helper
export const getSensorName = (
  sensorId: string,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  sensorEntries: BaseSensorEntry[]
) => {
  // Check if it's a derived value first
  if (sensorId.startsWith('derived_')) {
    const derivedValue = derivedValues.find((dv) => dv.id === sensorId)
    if (derivedValue) {
      return derivedValue.data.name
    }
  }

  // Otherwise look for a sensor
  const sensor = sensorEntries.find((e) => e.id === sensorId)
  return sensor?.data.name || sensorId
}

/**
 * Get all items to render (sensors + derived values with dependencies met)
 */
export const getAllItemsToRender = (
  selectedSensors: Array<{
    id: string
    name?: string
    description?: string
    unit?: string
  }>,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  sensorEntries: BaseSensorEntry[]
): Array<{
  id: string
  name: string
  isDerived: boolean
  description?: string
  unit?: string
}> => {
  const items: Array<{
    id: string
    name: string
    isDerived: boolean
    description?: string
    unit?: string
  }> = selectedSensors.map((sensor) => ({
    id: sensor.id,
    name: getSensorName(sensor.id, derivedValues, sensorEntries),
    isDerived: false,
    description: sensor.description,
    unit: sensor.unit,
  }))

  // Add derived values that have all dependencies selected
  derivedValues.forEach((derived) => {
    const allDependenciesSelected = derived.data.dependencyIds.every(
      (sensorId) => selectedSensors.some((selected) => selected.id === sensorId)
    )

    if (allDependenciesSelected) {
      items.push({
        id: derived.id,
        name: derived.data.name,
        isDerived: true,
        description: `Derived: ${derived.data.name}`,
        unit: derived.data.unit || '',
      })
    }
  })

  return items
}

/**
 * Apply threshold filters to chart data
 */
export const applyThresholdFilters = (
  data: ChartDataPoint[],
  filters: Record<string, ThresholdFilterConfig>
): ChartDataPoint[] => {
  if (!data.length) return data

  const activeFilters = Object.values(filters).filter((f) => f.enabled)
  if (!activeFilters.length) return data

  // For stability filters, we need to calculate CV for each point
  const stabilityFilters = activeFilters.filter(
    (f) => f.condition === 'stability'
  )
  const cvCache = new Map<string, Map<number, number>>() // sensorId -> index -> CV

  // Pre-calculate CV values for stability filters
  stabilityFilters.forEach((filter) => {
    const sensorCVs = new Map<number, number>()
    const windowSize = filter.value2 || 10

    data.forEach((_, index) => {
      const windowStart = Math.max(0, index - Math.floor(windowSize / 2))
      const windowEnd = Math.min(data.length, index + Math.ceil(windowSize / 2))

      const windowValues: number[] = []
      for (let i = windowStart; i < windowEnd; i++) {
        const value = data[i][filter.sensorId]
        if (typeof value === 'number') {
          windowValues.push(value)
        }
      }

      if (windowValues.length >= 2) {
        const mean =
          windowValues.reduce((a, b) => a + b, 0) / windowValues.length
        if (mean !== 0) {
          const variance =
            windowValues.reduce(
              (sum, val) => sum + Math.pow(val - mean, 2),
              0
            ) / windowValues.length
          const stdDev = Math.sqrt(variance)
          const cv = (stdDev / Math.abs(mean)) * 100
          sensorCVs.set(index, cv)
        }
      }
    })

    cvCache.set(filter.sensorId, sensorCVs)
  })

  return data.filter((point, index) => {
    // Check if all active threshold conditions are met
    return activeFilters.every((filter) => {
      const value = point[filter.sensorId]

      if (typeof value !== 'number') return false

      switch (filter.condition) {
        case 'above':
          return value > filter.value1
        case 'below':
          return value < filter.value1
        case 'between':
          return (
            filter.value2 !== null &&
            filter.value2 !== undefined &&
            value >= filter.value1 &&
            value <= filter.value2
          )
        case 'stability': {
          const cv = cvCache.get(filter.sensorId)?.get(index)
          return cv !== undefined && cv <= filter.value1
        }
        default:
          return true
      }
    })
  })
}

/**
 * Apply smoothing configurations to chart data
 */
export const applySmoothingConfigs = (
  data: ChartDataPoint[],
  configs: Record<string, SmoothingConfig>
): ChartDataPoint[] => {
  if (!data.length) return data

  const activeConfigs = Object.values(configs).filter((c) => c.enabled)
  if (!activeConfigs.length) return data

  // Create a copy of the data
  const smoothedData = data.map((point) => ({ ...point }))

  activeConfigs.forEach((config) => {
    const sensorId = config.sensorId

    // Apply smoothing to each sensor's data
    for (let i = 0; i < smoothedData.length; i++) {
      const windowStart = Math.max(0, i - Math.floor(config.windowSize / 2))
      const windowEnd = Math.min(
        smoothedData.length,
        i + Math.ceil(config.windowSize / 2)
      )

      const windowValues: number[] = []
      for (let j = windowStart; j < windowEnd; j++) {
        const value = data[j][sensorId]
        if (typeof value === 'number') {
          windowValues.push(value)
        }
      }

      if (windowValues.length > 0) {
        let smoothedValue: number
        windowValues.sort((a, b) => a - b)
        const mid = Math.floor(windowValues.length / 2)
        switch (config.method) {
          case 'avg':
            smoothedValue =
              windowValues.reduce((a, b) => a + b, 0) / windowValues.length
            break
          case 'max':
            smoothedValue = Math.max(...windowValues)
            break
          case 'min':
            smoothedValue = Math.min(...windowValues)
            break
          case 'median':
            smoothedValue =
              windowValues.length % 2 === 0
                ? (windowValues[mid - 1] + windowValues[mid]) / 2
                : windowValues[mid]
            break
          default:
            smoothedValue = windowValues[0]
        }
        smoothedData[i][sensorId] = smoothedValue
      }
    }
  })

  return smoothedData
}

/**
 * Apply derived values to chart data
 */
export const applyDerivedValues = (
  data: ChartDataPoint[],
  derivedVals: TimeSeriesDerivedValueEntryJSON[]
): ChartDataPoint[] => {
  if (!data.length || !derivedVals.length) return data

  return data.map((point) => {
    const newPoint = { ...point }
    derivedVals.forEach((derived) => {
      // Build context object with sensor values
      const context: Record<string, number> = {}

      derived.data.dependencyIds.forEach((sensorId) => {
        const value = newPoint[sensorId]

        if (typeof value === 'number') {
          // Use sanitized sensor ID as the variable name in expressions
          const sanitizedId = sensorId.replaceAll('-', '_').replaceAll('.', '_')
          context[sanitizedId] = value
        }
      })

      // Check if all required sensor values are present
      const requiredSensorCount = derived.data.dependencyIds.length
      const availableSensorCount = Object.keys(context).length

      if (availableSensorCount < requiredSensorCount) {
        return newPoint
      }

      // Evaluate the expression using math.js
      try {
        if ('timestamp' in context) {
          context.timestamp -= data[0].timestamp
        }
        const result = evaluate(derived.data.expression, context)

        if (typeof result === 'number' && isFinite(result)) {
          newPoint[derived.id] = result
        }
      } catch (error) {
        console.error(
          `Error evaluating expression "${derived.data.expression}":`,
          error,
          context
        )
      }
    })
    return newPoint
  })
}

/**
 * Resample data to improve performance - limit to specified points per trace
 */
export const resampleData = <T>(
  xValues: T[],
  yValues: (number | null)[],
  maxPoints: number = 1000
): { x: T[]; y: (number | null)[] } => {
  if (xValues.length <= maxPoints) {
    return { x: xValues, y: yValues }
  }

  // Calculate sampling interval
  const interval = Math.ceil(xValues.length / maxPoints)
  const resampledX: T[] = []
  const resampledY: (number | null)[] = []

  // Always include first point
  if (xValues.length > 0) {
    resampledX.push(xValues[0])
    resampledY.push(yValues[0])
  }

  // Sample points at regular intervals
  for (let i = interval; i < xValues.length - 1; i += interval) {
    resampledX.push(xValues[i])
    resampledY.push(yValues[i])
  }

  // Always include last point
  if (xValues.length > 1) {
    resampledX.push(xValues[xValues.length - 1])
    resampledY.push(yValues[yValues.length - 1])
  }

  return { x: resampledX, y: resampledY }
}
